import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import MemberLayout from "./components/MemberLayout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import StaffLogin from "./pages/StaffLogin";
import Dashboard from "./pages/Dashboard";
import Members from "./pages/Members";
import MemberCreate from "./pages/MemberCreate";
import MemberEdit from "./pages/MemberEdit";
import MemberDetails from "./pages/MemberDetails";
import Packages from "./pages/Packages";
import Memberships from "./pages/Memberships";
import MembershipDetails from "./pages/MembershipDetails";
import Payments from "./pages/Payments";
import Branches from "./pages/Branches";
import Staff from "./pages/Staff";
import Attendance from "./pages/Attendance";
import CalendarPage from "./pages/CalendarPage";
import ProgressCalendarPage from "./pages/ProgressCalendarPage";
import Settings from "./pages/Settings";
import Subscription from "./pages/Subscription";
import AdminDashboard from "./pages/admin/AdminDashboard";
import PublicSignup from "./pages/PublicSignup";
import PublicAttendance from "./pages/PublicAttendance";
import MembersOverview from "./pages/MembersOverview";
import MembershipsOverview from "./pages/MembershipsOverview";
import Leads from "./pages/Leads";
import WorkoutOverview from "./pages/WorkoutOverview";
import AllWorkouts from "./pages/AllWorkouts";
import MemberWorkoutCardBuilder from "./pages/MemberWorkoutCardBuilder";
import Followups from "./pages/Followups";
import LeadOverview from "./pages/leads/LeadOverview";
import AllLeads from "./pages/leads/AllLeads";
import LeadFollowups from "./pages/leads/LeadFollowups";
import WhatsAppConnectPage from "./pages/whatsapp/WhatsAppConnectPage";
import {
  MemberDashboard, MemberAttendance, MemberMembership,
  MemberWorkouts, MemberProgress, MemberProfile
} from "./pages/member";
import DeletedItems from "./pages/DeletedItems";
import { gymFromHost } from "./lib/gymAddress";

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function AccessDenied() {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-4">
      <div className="text-5xl">🔒</div>
      <h1 className="text-2xl font-bold font-heading">Access Denied</h1>
      <p className="text-muted-foreground text-center max-w-sm">
        You don't have permission to view this page. Please contact your gym manager.
      </p>
      <a href="/" className="text-primary underline text-sm">Go to Dashboard</a>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false, permission = null }) {
  const { user, loading, hasPermission } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== "super_admin") return <Navigate to="/" />;
  if (!adminOnly && user.role === "super_admin") return <Navigate to="/admin" />;
  const allowed = permission === null
    ? true
    : typeof permission === "function"
      ? permission(hasPermission, user)
      : hasPermission(permission);
  if (!allowed) return <AccessDenied />;
  return children;
}

function PublicAuthRoute({ children }) {
  const staffToken = localStorage.getItem("token");
  const memberToken = localStorage.getItem("member_token");
  const memberData = localStorage.getItem("member_data");
  const redirectTo = staffToken ? "/" : memberToken && memberData ? "/member/dashboard" : null;

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return children;
}

function MemberRoute({ children }) {
  const token  = localStorage.getItem("member_token");
  const member = localStorage.getItem("member_data");

  if (!token || !member) {
    return <Navigate to="/" replace />;
  }
  return <MemberLayout>{children}</MemberLayout>;
}

const HOST_GYM = gymFromHost();

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      {/* On a gym's subdomain, its staff and members sign in on its own page */}
      <Route path="/login" element={<PublicAuthRoute>{HOST_GYM ? <StaffLogin orgSlug={HOST_GYM} /> : <Login />}</PublicAuthRoute>} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/org/:orgSlug/login" element={<PublicAuthRoute><StaffLogin /></PublicAuthRoute>} />
      <Route path="/public/signup/:orgId/:branchId" element={<PublicSignup />} />
      <Route path="/public/attendance/:orgId/:branchId" element={<PublicAttendance />} />

      {/* Member Portal */}
      <Route path="/member/dashboard"  element={<MemberRoute><MemberDashboard /></MemberRoute>} />
      <Route path="/member/attendance" element={<MemberRoute><MemberAttendance /></MemberRoute>} />
      <Route path="/member/membership" element={<MemberRoute><MemberMembership /></MemberRoute>} />
      <Route path="/member/workouts"   element={<MemberRoute><MemberWorkouts /></MemberRoute>} />
      <Route path="/member/progress"   element={<MemberRoute><MemberProgress /></MemberRoute>} />
      <Route path="/member/profile"    element={<MemberRoute><MemberProfile /></MemberRoute>} />

      {/* Dashboard */}
      <Route path="/" element={
        <ProtectedRoute permission="view_dashboard">
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Members */}
      <Route path="/members" element={
        <ProtectedRoute permission={(hp) => hp("view_members") || hp("view_own_members")}>
          <Layout><Members /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/members/new"      element={<ProtectedRoute permission="create_member"><Layout><MemberCreate /></Layout></ProtectedRoute>} />
      <Route path="/members/overview" element={<ProtectedRoute permission="members_overview"><Layout><MembersOverview /></Layout></ProtectedRoute>} />
      <Route path="/members/expired"  element={<Navigate to="/members?status=Expired" replace />} />
      <Route path="/members/:id"      element={
        <ProtectedRoute permission={(hp) => hp("view_members") || hp("view_own_members")}>
          <Layout><MemberDetails /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/members/:id/workouts/new" element={
        <ProtectedRoute permission="add_workout">
          <Layout><MemberWorkoutCardBuilder /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/members/:id/edit" element={<ProtectedRoute permission="edit_member"><Layout><MemberEdit /></Layout></ProtectedRoute>} />

      {/* Followups */}
      <Route path="/followups" element={
        <ProtectedRoute permission={(hp) => hp("view_followups") || hp("manage_followups")}>
          <Layout><Followups /></Layout>
        </ProtectedRoute>
      } />

      {/* Leads */}
      <Route path="/leads"           element={<ProtectedRoute permission="view_leads"><Layout><LeadOverview /></Layout></ProtectedRoute>} />
      <Route path="/leads/overview"  element={<ProtectedRoute permission="view_leads"><Layout><LeadOverview /></Layout></ProtectedRoute>} />
      <Route path="/leads/all"       element={<ProtectedRoute permission="view_leads"><Layout><AllLeads /></Layout></ProtectedRoute>} />
      <Route path="/leads/followups" element={<ProtectedRoute permission="view_leads"><Layout><LeadFollowups /></Layout></ProtectedRoute>} />

      {/* Workouts */}
      <Route path="/workouts/overview" element={<ProtectedRoute permission="view_workouts"><Layout><WorkoutOverview /></Layout></ProtectedRoute>} />
      <Route path="/workouts"          element={<ProtectedRoute permission="view_workouts"><Layout><AllWorkouts /></Layout></ProtectedRoute>} />

      {/* Packages */}
      <Route path="/packages" element={<ProtectedRoute permission="view_packages"><Layout><Packages /></Layout></ProtectedRoute>} />

      {/* Memberships */}
      <Route path="/memberships"          element={<ProtectedRoute permission="view_memberships"><Layout><Memberships /></Layout></ProtectedRoute>} />
      <Route path="/memberships/overview" element={<ProtectedRoute permission="view_memberships"><Layout><MembershipsOverview /></Layout></ProtectedRoute>} />
      <Route path="/memberships/payments" element={<ProtectedRoute permission="view_payments"><Layout><Payments /></Layout></ProtectedRoute>} />
      <Route path="/memberships/:id"      element={<ProtectedRoute permission="view_memberships"><Layout><MembershipDetails /></Layout></ProtectedRoute>} />

      {/* Org management */}
      <Route path="/branches"     element={<ProtectedRoute permission="view_branches"><Layout><Branches /></Layout></ProtectedRoute>} />
      <Route path="/staff"        element={<ProtectedRoute permission="view_staff"><Layout><Staff /></Layout></ProtectedRoute>} />
      <Route path="/settings"     element={<ProtectedRoute permission="view_settings"><Layout><Settings /></Layout></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute permission="view_subscription"><Layout><Subscription /></Layout></ProtectedRoute>} />

      {/* Attendance & Calendar */}
      <Route path="/attendance"        element={<ProtectedRoute permission="view_attendance"><Layout><Attendance /></Layout></ProtectedRoute>} />
      <Route path="/calendar"          element={<ProtectedRoute permission="view_calendar"><Layout><CalendarPage /></Layout></ProtectedRoute>} />
      <Route path="/progress-calendar" element={<ProtectedRoute permission="view_progress"><Layout><ProgressCalendarPage /></Layout></ProtectedRoute>} />

      {/* WhatsApp */}
      <Route path="/whatsapp"             element={<ProtectedRoute permission="view_whatsapp"><Layout><WhatsAppConnectPage /></Layout></ProtectedRoute>} />
      <Route path="/whatsapp/overview"    element={<ProtectedRoute permission="view_whatsapp"><Layout><WhatsAppConnectPage /></Layout></ProtectedRoute>} />

      {/* Deleted Items — owner only */}
      <Route path="/deleted-items" element={
        <ProtectedRoute permission={(hp, u) => u?.role === "owner"}>
          <Layout><DeletedItems /></Layout>
        </ProtectedRoute>
      } />

      {/* Super admin */}
      <Route path="/admin/*" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
